// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect } from "react";
import { apiService } from "../services/api/apiService";
import { ApiServiceError } from "../services/api/apiError";
import { getTenantSettings } from "../features/onboarding/api/tenantSettingsApi";
import { startActivityHeartbeat } from "../utils/userActivity";
import { useAuthSession } from "./useAuthSession";

export const STUB_TENANT_ID = "tenant-current";

export interface HeartbeatSenderDeps {
  getAccessToken: () => Promise<string | null | undefined>;
  resolveTenantId: (token: string) => Promise<string | undefined>;
  post: (token: string, tenantId: string) => Promise<unknown>;
}

function isRealTenantId(tenantId: string | undefined): tenantId is string {
  return typeof tenantId === "string" && tenantId.trim().length > 0 && tenantId !== STUB_TENANT_ID;
}

export function createHeartbeatSender(deps: HeartbeatSenderDeps): () => Promise<void> {
  let tenantId: string | undefined;

  return async () => {
    const token = await deps.getAccessToken();
    if (!token) {
      return;
    }

    if (!isRealTenantId(tenantId)) {
      const resolved = await deps.resolveTenantId(token);
      if (!isRealTenantId(resolved)) {
        tenantId = undefined;
        return;
      }
      tenantId = resolved;
    }

    try {
      await deps.post(token, tenantId);
    } catch (error) {
      if (error instanceof ApiServiceError && error.status === 403) {
        tenantId = undefined;
      }
      throw error;
    }
  };
}

export function useActivityHeartbeat(enabled: boolean): void {
  const { getAccessToken } = useAuthSession();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return startActivityHeartbeat({
      send: createHeartbeatSender({
        getAccessToken: async () => (await getAccessToken()) ?? undefined,
        resolveTenantId: async (token) =>
          (await getTenantSettings({ token, refresh: true })).tenantId,
        post: (token, tenantId) =>
          apiService.requestJson<unknown>({
            path: "/api/activity/heartbeat",
            method: "POST",
            headers: { "X-Tenant-Id": tenantId },
            token,
          }),
      }),
    });
  }, [enabled, getAccessToken]);
}
